import React, { useState, useEffect } from "react";
import { Receipt, Download, Trash, Check, Plus, FileText, ArrowRight, Eye, PencilSimple, X, EnvelopeSimple } from "@phosphor-icons/react";
import { api, formatCurrency, formatDate } from "../../utils/api";
import { Button } from "../shared/Button";
import { Badge } from "../shared/Badge";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Modal } from "../shared/Modal";
import { Input, Textarea, Select } from "../shared/Input";
import { AnimatePresence, motion } from "framer-motion";

export const Facturas = ({ adminToken }) => {
  const [facturas, setFacturas] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalConvertir, setModalConvertir] = useState(false);
  const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
  const [facturaVistaPrevia, setFacturaVistaPrevia] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [facturaEditada, setFacturaEditada] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [facturasData, presupuestosData] = await Promise.all([
        api.get("/facturas", adminToken),
        api.get("/presupuestos", adminToken)
      ]);
      setFacturas(facturasData);
      // Filtrar solo presupuestos aceptados que no tengan factura
      const presupuestosSinFactura = presupuestosData.filter(p => 
        p.estado === "aceptado" && !facturasData.some(f => f.presupuesto_id === p.id)
      );
      setPresupuestos(presupuestosSinFactura);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const convertirAFactura = async () => {
    if (!presupuestoSeleccionado) return;
    try {
      await api.post("/facturas/convertir", { presupuesto_id: presupuestoSeleccionado }, adminToken);
      setModalConvertir(false);
      setPresupuestoSeleccionado(null);
      cargarDatos();
    } catch (e) {
      console.error("Error:", e);
      alert("Error al convertir a factura: " + e.message);
    }
  };

  const marcarCobrada = async (id, cobrada) => {
    try {
      await api.put(`/facturas/${id}`, { cobrada: !cobrada }, adminToken);
      cargarDatos();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const generarPDF = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/facturas/${id}/pdf`, {
        headers: { "Authorization": `Bearer ${adminToken}` }
      });
      if (!response.ok) throw new Error("No se pudo generar el PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      console.error("Error:", e);
      alert("Error al generar PDF");
    }
  };

  const enviarEmail = async (factura) => {
    const email = prompt("Email del cliente:", factura.cliente_email || "");
    if (!email) return;
    
    try {
      const response = await api.post(`/facturas/${factura.id}/email`, { email }, adminToken);
      alert(response.message || "Email enviado (función en desarrollo - descarga PDF y envíalo manualmente)");
    } catch (e) {
      console.error("Error:", e);
      alert("Error al enviar email: " + (e.message || "Error desconocido"));
    }
  };

  const eliminarFactura = async (id) => {
    if (!window.confirm("¿Eliminar factura?")) return;
    try {
      await api.delete(`/facturas/${id}`, adminToken);
      cargarDatos();
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const abrirVistaPrevia = (factura) => {
    setFacturaVistaPrevia(factura);
    setFacturaEditada(JSON.parse(JSON.stringify(factura))); // Deep clone
    setModoEdicion(false);
  };

  const cerrarVistaPrevia = () => {
    setFacturaVistaPrevia(null);
    setFacturaEditada(null);
    setModoEdicion(false);
  };

  const activarEdicion = () => {
    setModoEdicion(true);
  };

  const guardarCambios = async () => {
    try {
      await api.put(`/facturas/${facturaEditada.id}`, facturaEditada, adminToken);
      setModoEdicion(false);
      cargarDatos();
      alert("✅ Factura actualizada correctamente");
      cerrarVistaPrevia();
    } catch (e) {
      console.error("Error:", e);
      alert("❌ Error al guardar cambios: " + e.message);
    }
  };

  const actualizarLineaEditada = (index, campo, valor) => {
    const nuevasLineas = [...facturaEditada.lineas];
    nuevasLineas[index] = { ...nuevasLineas[index], [campo]: parseFloat(valor) || 0 };
    // Recalcular subtotal
    nuevasLineas[index].subtotal = nuevasLineas[index].cantidad * nuevasLineas[index].precio_unitario;
    
    // Recalcular total
    const nuevoTotal = nuevasLineas.reduce((sum, l) => sum + l.subtotal, 0);
    setFacturaEditada({
      ...facturaEditada,
      lineas: nuevasLineas,
      total: nuevoTotal
    });
  };

  const eliminarLineaEditada = (index) => {
    const nuevasLineas = facturaEditada.lineas.filter((_, i) => i !== index);
    const nuevoTotal = nuevasLineas.reduce((sum, l) => sum + l.subtotal, 0);
    setFacturaEditada({
      ...facturaEditada,
      lineas: nuevasLineas,
      total: nuevoTotal
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-h2 font-display mb-1">💰 Facturas</h1>
            <p className="text-small text-secondary">{facturas.length} facturas emitidas</p>
          </div>
          {presupuestos.length > 0 && (
            <Button variant="accent" onClick={() => setModalConvertir(true)} icon={<Plus size={20} />}>
              Convertir Presupuesto
            </Button>
          )}
        </div>

        {/* Estadísticas rápidas */}
        {facturas.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="card p-4">
              <div className="text-tiny text-secondary mb-1">Total Facturado</div>
              <div className="text-h3 font-display" style={{ color: "var(--brand-accent)" }}>
                {formatCurrency(facturas.reduce((sum, f) => sum + (f.total || 0), 0))}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-tiny text-secondary mb-1">Pendientes de Cobro</div>
              <div className="text-h3 font-display" style={{ color: "var(--warning)" }}>
                {formatCurrency(facturas.filter(f => !f.cobrada).reduce((sum, f) => sum + (f.total || 0), 0))}
              </div>
            </div>
          </div>
        )}

        {facturas.length === 0 ? (
          <EmptyState 
            icon="🧾" 
            title="Sin facturas" 
            description="Convierte presupuestos aceptados en facturas"
            action={presupuestos.length > 0 && (
              <Button variant="accent" onClick={() => setModalConvertir(true)}>
                Convertir Presupuesto
              </Button>
            )}
          />
        ) : (
          <div className="space-y-3">
            {facturas.map(factura => (
              <div key={factura.id} className="card p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-bold text-small">{factura.numero}</div>
                    <div className="text-tiny text-secondary">{factura.cliente}</div>
                    {factura.presupuesto_numero && (
                      <div className="text-tiny text-secondary flex items-center gap-1 mt-1">
                        <FileText size={12} />
                        De presupuesto {factura.presupuesto_numero}
                      </div>
                    )}
                  </div>
                  <Badge variant={factura.cobrada ? "success" : "warning"}>
                    {factura.cobrada ? "✓ Cobrada" : "⏳ Pendiente"}
                  </Badge>
                </div>
                <div className="flex justify-between mb-3">
                  <div className="text-tiny text-secondary">{formatDate(factura.fecha)}</div>
                  <div className="font-bold" style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency(factura.total)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={() => abrirVistaPrevia(factura)} 
                    icon={<Eye size={16} />}
                  >
                    Vista Previa
                  </Button>
                  <Button 
                    variant={factura.cobrada ? "outline" : "success"} 
                    size="sm" 
                    onClick={() => marcarCobrada(factura.id, factura.cobrada)} 
                    icon={<Check size={16} />}
                  >
                    {factura.cobrada ? "Pendiente" : "Cobrada"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => generarPDF(factura.id)} icon={<Download size={16} />}>
                    PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => enviarEmail(factura)} icon={<EnvelopeSimple size={16} />}>
                    Email
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => eliminarFactura(factura.id)} icon={<Trash size={16} />}>
                    Eliminar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Convertir Presupuesto */}
      <Modal isOpen={modalConvertir} onClose={() => setModalConvertir(false)} title="Convertir Presupuesto a Factura">
        <div>
          <div className="card p-3 mb-4" style={{ background: "var(--info-bg)", border: "1px solid var(--info)" }}>
            <div className="text-tiny">
              💡 Selecciona un presupuesto aceptado para convertirlo en factura automáticamente
            </div>
          </div>
          
          {presupuestos.length === 0 ? (
            <div className="text-center text-small text-secondary py-6">
              No hay presupuestos aceptados disponibles para convertir
            </div>
          ) : (
            <>
              <label className="text-label mb-2 block">Presupuesto</label>
              <select
                value={presupuestoSeleccionado || ""}
                onChange={(e) => setPresupuestoSeleccionado(e.target.value)}
                className="w-full p-3 rounded-lg border border-subtle bg-surface-secondary mb-4"
              >
                <option value="">Selecciona un presupuesto...</option>
                {presupuestos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.numero} - {p.cliente_nombre} - {formatCurrency(p.total_final ?? p.total)}
                    {p.tipo_presupuesto === "mo_material" ? " (MO + materiales)" : ""}
                  </option>
                ))}
              </select>

              <Button
                variant="accent"
                className="btn-full"
                onClick={convertirAFactura}
                disabled={!presupuestoSeleccionado}
                icon={<ArrowRight size={20} />}
              >
                Convertir a Factura
              </Button>
            </>
          )}
        </div>
      </Modal>

      {/* Modal Vista Previa y Edición */}
      <AnimatePresence>
        {facturaVistaPrevia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={cerrarVistaPrevia}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              className="modal-content"
              style={{ maxWidth: "800px", maxHeight: "90vh", overflow: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-subtle">
                <div>
                  <h2 className="text-h2 font-display">{facturaVistaPrevia.numero}</h2>
                  <div className="text-small text-secondary">
                    {modoEdicion ? "🔧 Modo Edición" : "👁️ Vista Previa"}
                  </div>
                </div>
                <div className="flex gap-2">
                  {!modoEdicion ? (
                    <>
                      <Button variant="primary" size="sm" onClick={activarEdicion} icon={<PencilSimple size={16} />}>
                        Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => generarPDF(facturaVistaPrevia.id)} icon={<Download size={16} />}>
                        PDF
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="success" size="sm" onClick={guardarCambios} icon={<Check size={16} />}>
                        Guardar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setModoEdicion(false)}>
                        Cancelar
                      </Button>
                    </>
                  )}
                  <button className="icon-button" onClick={cerrarVistaPrevia}>
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Cliente */}
              <div className="card p-4 mb-4" style={{ background: "#F0F9FF" }}>
                <div className="text-label mb-2">👤 CLIENTE</div>
                <div className="font-bold">{facturaVistaPrevia.cliente}</div>
                <div className="text-small text-secondary">{formatDate(facturaVistaPrevia.fecha)}</div>
                {facturaVistaPrevia.presupuesto_numero && (
                  <div className="text-tiny text-secondary mt-1">
                    📄 Presupuesto: {facturaVistaPrevia.presupuesto_numero}
                  </div>
                )}
              </div>

              {/* Líneas de factura */}
              <div className="card p-4 mb-4">
                <div className="text-label mb-3">📋 CONCEPTOS FACTURADOS</div>
                {(modoEdicion ? facturaEditada : facturaVistaPrevia).lineas?.map((linea, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b border-subtle last:border-0">
                    {modoEdicion ? (
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={linea.trabajo_nombre || linea.concepto}
                            onChange={(e) => {
                              const nuevasLineas = [...facturaEditada.lineas];
                              nuevasLineas[idx].trabajo_nombre = e.target.value;
                              setFacturaEditada({ ...facturaEditada, lineas: nuevasLineas });
                            }}
                            className="input flex-1"
                            placeholder="Concepto"
                          />
                          <button
                            onClick={() => eliminarLineaEditada(idx)}
                            className="btn-icon-sm"
                            style={{ background: "rgba(193,41,46,0.1)", color: "var(--error)" }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="number"
                            value={linea.cantidad}
                            onChange={(e) => actualizarLineaEditada(idx, "cantidad", e.target.value)}
                            className="input"
                            placeholder="Cantidad"
                            step="0.1"
                          />
                          <input
                            type="number"
                            value={linea.precio_unitario}
                            onChange={(e) => actualizarLineaEditada(idx, "precio_unitario", e.target.value)}
                            className="input"
                            placeholder="Precio unit."
                            step="0.5"
                          />
                          <div className="input" style={{ background: "#f0f0f0", color: "#666" }}>
                            {formatCurrency(linea.subtotal)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between mb-1">
                          <div className="font-bold">{linea.trabajo_nombre || linea.concepto}</div>
                          <div className="font-bold">{formatCurrency(linea.subtotal)}</div>
                        </div>
                        <div className="text-small text-secondary">
                          {linea.cantidad} {linea.unidad} × {formatCurrency(linea.precio_unitario)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Materiales (si el presupuesto original los incluía) — antes esta
                  sección no existía y los materiales quedaban invisibles en toda
                  la pantalla de Facturas aunque el cliente los hubiera aceptado. */}
              {(modoEdicion ? facturaEditada : facturaVistaPrevia).materiales_necesarios?.length > 0 && (
                <div className="card p-4 mb-4">
                  <div className="text-label mb-3">📦 MATERIALES INCLUIDOS</div>
                  {(modoEdicion ? facturaEditada : facturaVistaPrevia).materiales_necesarios.map((mat, idx) => (
                    <div key={idx} className="mb-2 pb-2 border-b border-subtle last:border-0">
                      <div className="flex justify-between mb-1">
                        <div className="font-bold">{mat.material_nombre}{mat.marca ? ` (${mat.marca})` : ""}</div>
                        <div className="font-bold">{formatCurrency(mat.subtotal)}</div>
                      </div>
                      <div className="text-small text-secondary">
                        {mat.descripcion_comercial || `${mat.cantidad_calculada ?? mat.cantidad ?? 0} ${mat.material_unidad || ""}`}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between mt-2 pt-2 border-t border-subtle text-small font-bold">
                    <div>Subtotal materiales</div>
                    <div>{formatCurrency((modoEdicion ? facturaEditada : facturaVistaPrevia).total_materiales)}</div>
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="card p-4 mb-4" style={{ background: "var(--brand-primary)", color: "white" }}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-small" style={{ opacity: 0.9 }}>TOTAL FACTURA</div>
                    {facturaVistaPrevia.cobrada ? (
                      <div className="text-tiny" style={{ opacity: 0.7 }}>✅ Cobrada</div>
                    ) : (
                      <div className="text-tiny" style={{ opacity: 0.7 }}>⏳ Pendiente de cobro</div>
                    )}
                  </div>
                  <div className="text-hero font-display" style={{ color: "var(--brand-accent)" }}>
                    {formatCurrency((modoEdicion ? facturaEditada : facturaVistaPrevia).total)}
                  </div>
                </div>
              </div>

              {/* Acciones */}
              {!modoEdicion && (
                <div className="flex gap-3">
                  <Button
                    variant={facturaVistaPrevia.cobrada ? "outline" : "success"}
                    className="flex-1"
                    onClick={() => {
                      marcarCobrada(facturaVistaPrevia.id, facturaVistaPrevia.cobrada);
                      cerrarVistaPrevia();
                    }}
                    icon={<Check size={20} />}
                  >
                    {facturaVistaPrevia.cobrada ? "Marcar Pendiente" : "Marcar Cobrada"}
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1"
                    onClick={() => generarPDF(facturaVistaPrevia.id)}
                    icon={<Download size={20} />}
                  >
                    Descargar PDF
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};